import React from 'react';
import { cn } from '@/lib/utils.js';

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-slate-200/80 dark:bg-brand-tint/30',
        className
      )}
      {...props}
    />
  );
}

export function SkeletonStat() {
  return (
    <div className="rounded-card border border-brand-border bg-surface p-5 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
      <Skeleton className="h-7 w-20" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-card border border-brand-border bg-surface p-5 space-y-4", className)}>
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1 pr-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      <Skeleton className="h-2 w-full rounded-full" />
      <div className="flex items-center justify-between pt-2 border-t border-brand-border/40">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-6 w-6 rounded-full" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="w-full rounded-card border border-brand-border bg-surface overflow-hidden shadow-card">
      <div className="border-b border-brand-border bg-slate-50/60 dark:bg-[#0E1A18] px-4 py-3 flex items-center justify-between gap-4">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1 max-w-[120px]" />
        ))}
      </div>
      <div className="divide-y divide-brand-border/40">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="px-4 py-3.5 flex items-center justify-between gap-4">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton
                key={c}
                className={cn(
                  "h-3 flex-1",
                  c === 0 ? "max-w-[180px] font-medium" : "max-w-[100px]"
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonKanban({ columns = 5 }: { columns?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {Array.from({ length: columns }).map((_, c) => (
        <div key={c} className="rounded-xl border border-brand-border bg-surface/50 p-3 space-y-3">
          <div className="flex items-center justify-between px-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-6 rounded-full" />
          </div>
          <div className="space-y-2.5">
            {Array.from({ length: c % 2 === 0 ? 3 : 2 }).map((_, cardIdx) => (
              <div
                key={cardIdx}
                className="rounded-lg border border-brand-border bg-surface p-3.5 space-y-3 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-16 rounded" />
                  <Skeleton className="h-3 w-8" />
                </div>
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-2 w-full rounded-full" />
                <div className="flex items-center justify-between pt-1">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-3 w-14" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTimeline({ items = 6 }: { items?: number }) {
  return (
    <div className="rounded-card border border-brand-border bg-surface p-6 space-y-6 shadow-card">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-start space-x-4">
          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-5 w-28 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
